import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Settings, ArrowLeft, Save, Mail, Calendar, Clock, FileText, Send,
  CheckCircle, Loader2, Building2, User, Link2, TestTube, AlertTriangle, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ComptabiliteParametres() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [settings, setSettings] = useState({
    send_day: 15,
    send_hour: 8,
    send_minute: 0,
    auto_send_enabled: true,
    sender_email: '',
    expert_name: '',
    expert_email: '',
    cc_email: '',
    custom_message: '',
    include_sales_invoices: true,
    include_purchase_invoices: true,
    include_bank_statements: true,
    include_sepa_remises: true,
    include_payslips: true,
    include_contracts: false,
    include_expense_reports: false,
    link_validity_days: 7,
    send_if_incomplete: true,
    self_alert_enabled: false,
    self_alert_day: 5,
    self_alert_hour: 9,
    self_alert_minute: 0,
    self_alert_email: ''
  });

  const token = localStorage.getItem('token');

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/comptabilite/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/comptabilite/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async () => {
    if (!settings.expert_email) {
      toast.error('Configurez d\'abord l\'email de l\'expert comptable');
      return;
    }
    
    setTesting(true);
    try {
      const res = await axios.post(`${API}/api/comptabilite/test-send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test envoyé à ${res.data.sent_to?.join(', ')}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi test');
    } finally {
      setTesting(false);
    }
  };

  const handleTestSelfAlert = async () => {
    if (!settings.self_alert_email) {
      toast.error('Renseignez d\'abord l\'email de l\'alerte');
      return;
    }
    setTestingAlert(true);
    try {
      await axios.put(`${API}/api/comptabilite/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const res = await axios.post(`${API}/api/comptabilite/self-alert/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Alerte test envoyée à ${res.data.sent_to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi test');
    } finally {
      setTestingAlert(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/comptabilite')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Settings className="w-6 h-6 text-blue-500" />
              Paramètres Comptabilité
            </h1>
            <p className="text-slate-500 text-sm">Configuration de l'envoi des dossiers à l'expert comptable</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleTestSend} disabled={testing || !settings.expert_email}>
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TestTube className="w-4 h-4 mr-2" />}
            Envoyer un test
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Envoi automatique */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Envoi automatique
          </CardTitle>
          <CardDescription>Planifiez l'envoi mensuel du dossier comptable</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="auto-send" className="flex flex-col">
              <span className="font-medium">Activer l'envoi automatique</span>
              <span className="text-xs text-slate-500">Le dossier sera envoyé automatiquement chaque mois</span>
            </Label>
            <Switch
              id="auto-send"
              checked={settings.auto_send_enabled}
              onCheckedChange={(checked) => updateSetting('auto_send_enabled', checked)}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Jour d'envoi (1-28)</Label>
              <Select
                value={String(settings.send_day)}
                onValueChange={(val) => updateSetting('send_day', parseInt(val))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                    <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Heure d'envoi</Label>
              <Select
                value={String(settings.send_hour)}
                onValueChange={(val) => updateSetting('send_hour', parseInt(val))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => i).map(hour => (
                    <SelectItem key={hour} value={String(hour)}>{String(hour).padStart(2, '0')}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <Label htmlFor="send-incomplete" className="flex flex-col">
              <span className="font-medium">Envoyer même si incomplet</span>
              <span className="text-xs text-slate-500">Si désactivé, l'envoi sera bloqué si des documents manquent</span>
            </Label>
            <Switch
              id="send-incomplete"
              checked={settings.send_if_incomplete}
              onCheckedChange={(checked) => updateSetting('send_if_incomplete', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Alerte récupération documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Alerte "Récupérer mes documents"
          </CardTitle>
          <CardDescription>Recevez chaque mois un email de rappel pour rassembler vos documents comptables, à la date et l'heure de votre choix</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="self-alert" className="flex flex-col">
              <span className="font-medium">Activer l'alerte mensuelle</span>
              <span className="text-xs text-slate-500">Un email vous sera envoyé le jour et à l'heure configurés (heure de Paris)</span>
            </Label>
            <Switch
              id="self-alert"
              data-testid="self-alert-switch"
              checked={settings.self_alert_enabled}
              onCheckedChange={(checked) => updateSetting('self_alert_enabled', checked)}
            />
          </div>

          {settings.self_alert_enabled && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Jour du mois (1-28)</Label>
                  <Select
                    value={String(settings.self_alert_day)}
                    onValueChange={(val) => updateSetting('self_alert_day', parseInt(val))}
                  >
                    <SelectTrigger data-testid="self-alert-day-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                        <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Heure</Label>
                  <Select
                    value={String(settings.self_alert_hour)}
                    onValueChange={(val) => updateSetting('self_alert_hour', parseInt(val))}
                  >
                    <SelectTrigger data-testid="self-alert-hour-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => i).map(hour => (
                        <SelectItem key={hour} value={String(hour)}>{String(hour).padStart(2, '0')}h</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Minutes</Label>
                  <Select
                    value={String(settings.self_alert_minute)}
                    onValueChange={(val) => updateSetting('self_alert_minute', parseInt(val))}
                  >
                    <SelectTrigger data-testid="self-alert-minute-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 15, 30, 45].map(min => (
                        <SelectItem key={min} value={String(min)}>{String(min).padStart(2, '0')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="self-alert-email">Email destinataire de l'alerte *</Label>
                <Input
                  id="self-alert-email"
                  data-testid="self-alert-email-input"
                  type="email"
                  placeholder="vous@votreentreprise.fr"
                  value={settings.self_alert_email || ''}
                  onChange={(e) => updateSetting('self_alert_email', e.target.value)}
                />
              </div>

              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={handleTestSelfAlert} disabled={testingAlert || !settings.self_alert_email} data-testid="self-alert-test-btn">
                  {testingAlert ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TestTube className="w-4 h-4 mr-2" />}
                  Tester l'alerte maintenant
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Email d'envoi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-500" />
            Expéditeur
          </CardTitle>
          <CardDescription>Adresse email utilisée pour l'envoi des dossiers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sender-email">Email d'envoi *</Label>
            <Input
              id="sender-email"
              type="email"
              placeholder="comptabilite@votreentreprise.fr"
              value={settings.sender_email || ''}
              onChange={(e) => updateSetting('sender_email', e.target.value)}
            />
            <p className="text-xs text-slate-500">
              Cet email doit être vérifié dans Resend. Les emails seront envoyés depuis cette adresse.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Destinataire */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-green-500" />
            Destinataire
          </CardTitle>
          <CardDescription>Informations de l'expert comptable</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expert-name">Nom de l'expert comptable</Label>
              <Input
                id="expert-name"
                placeholder="Cabinet Dupont"
                value={settings.expert_name}
                onChange={(e) => updateSetting('expert_name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expert-email">Email de l'expert comptable *</Label>
              <Input
                id="expert-email"
                type="email"
                placeholder="expert@cabinet.fr"
                value={settings.expert_email}
                onChange={(e) => updateSetting('expert_email', e.target.value)}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="cc-email">Emails en copie (CC) - optionnel</Label>
            <Input
              id="cc-email"
              type="text"
              placeholder="email1@exemple.fr, email2@exemple.fr"
              value={settings.cc_email || ''}
              onChange={(e) => updateSetting('cc_email', e.target.value)}
            />
            <p className="text-xs text-gray-500">
              Plusieurs emails possibles, séparés par des virgules
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="custom-message">Corps de l'email</Label>
            <Textarea
              id="custom-message"
              placeholder="Bonjour,

Veuillez trouver le dossier comptable de {mois} {annee} à télécharger via ce lien :

{lien}

Cordialement"
              value={settings.custom_message}
              onChange={(e) => updateSetting('custom_message', e.target.value)}
              rows={6}
            />
            <div className="text-xs text-slate-500 space-y-1">
              <p className="font-medium">Variables disponibles :</p>
              <p><code className="bg-slate-100 px-1 rounded">{'{mois}'}</code> - Nom du mois (ex: Avril)</p>
              <p><code className="bg-slate-100 px-1 rounded">{'{annee}'}</code> - Année (ex: 2026)</p>
              <p><code className="bg-slate-100 px-1 rounded">{'{entreprise}'}</code> - Nom de l'entreprise</p>
              <p><code className="bg-slate-100 px-1 rounded">{'{lien}'}</code> - Lien cliquable de téléchargement</p>
              <p><code className="bg-slate-100 px-1 rounded">{'{nb_fichiers}'}</code> - Nombre de fichiers</p>
              <p><code className="bg-slate-100 px-1 rounded">{'{message}'}</code> - Message du mois (saisi sur la page du dossier mensuel)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contenu du dossier */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-500" />
            Contenu du dossier
          </CardTitle>
          <CardDescription>Sélectionnez les documents à inclure automatiquement</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="include-sales"
                checked={settings.include_sales_invoices}
                onCheckedChange={(checked) => updateSetting('include_sales_invoices', checked)}
              />
              <Label htmlFor="include-sales" className="flex-1 cursor-pointer">
                <span className="font-medium">Factures ventes</span>
                <p className="text-xs text-slate-500">PDFs des factures clients émises</p>
              </Label>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="include-purchases"
                checked={settings.include_purchase_invoices}
                onCheckedChange={(checked) => updateSetting('include_purchase_invoices', checked)}
              />
              <Label htmlFor="include-purchases" className="flex-1 cursor-pointer">
                <span className="font-medium">Factures achats</span>
                <p className="text-xs text-slate-500">PDFs des factures fournisseurs</p>
              </Label>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="include-bank"
                checked={settings.include_bank_statements}
                onCheckedChange={(checked) => updateSetting('include_bank_statements', checked)}
              />
              <Label htmlFor="include-bank" className="flex-1 cursor-pointer">
                <span className="font-medium">Relevé bancaire</span>
                <p className="text-xs text-slate-500">PDF du relevé de compte mensuel</p>
              </Label>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="include-sepa"
                checked={settings.include_sepa_remises}
                onCheckedChange={(checked) => updateSetting('include_sepa_remises', checked)}
              />
              <Label htmlFor="include-sepa" className="flex-1 cursor-pointer">
                <span className="font-medium">Remises SEPA</span>
                <p className="text-xs text-slate-500">PDFs des bordereaux de remise</p>
              </Label>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="include-payslips"
                checked={settings.include_payslips}
                onCheckedChange={(checked) => updateSetting('include_payslips', checked)}
              />
              <Label htmlFor="include-payslips" className="flex-1 cursor-pointer">
                <span className="font-medium">Bulletins de salaire</span>
                <p className="text-xs text-slate-500">PDFs des fiches de paie du mois</p>
              </Label>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="include-contracts"
                checked={settings.include_contracts}
                onCheckedChange={(checked) => updateSetting('include_contracts', checked)}
              />
              <Label htmlFor="include-contracts" className="flex-1 cursor-pointer">
                <span className="font-medium">Contrats signés</span>
                <p className="text-xs text-slate-500">Contrats signés ce mois (optionnel)</p>
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Validité du lien */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-orange-500" />
            Lien de téléchargement
          </CardTitle>
          <CardDescription>Configuration du lien sécurisé envoyé à l'expert</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Durée de validité du lien (jours)</Label>
            <Select
              value={String(settings.link_validity_days)}
              onValueChange={(val) => updateSetting('link_validity_days', parseInt(val))}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 3, 7, 14, 30].map(days => (
                  <SelectItem key={days} value={String(days)}>{days} jour{days > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Après cette période, l'expert devra demander un nouveau lien
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Zone Danger */}
      <Card className="border-red-200 bg-red-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Zone danger
          </CardTitle>
          <CardDescription className="text-red-600">
            Actions irréversibles - Utilisez avec précaution
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-200">
            <div>
              <p className="font-medium text-slate-900">Réinitialiser l'historique comptable</p>
              <p className="text-sm text-slate-500 mt-1">
                Supprime tous les dossiers avant le 1er mars 2026.<br />
                Cette action est irréversible et supprimera les fichiers manuels associés.
              </p>
              {settings.accounting_start_date && (
                <p className="text-xs text-green-600 mt-2">
                  Date de démarrage actuelle : {new Date(settings.accounting_start_date).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                </p>
              )}
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={resetting}>
                  {resetting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Réinitialiser
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmer la réinitialisation</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action va supprimer définitivement tous les dossiers comptables 
                    antérieurs au 1er mars 2026, ainsi que les fichiers manuels qui leur 
                    sont associés.
                    <br /><br />
                    <strong className="text-red-600">Cette action est irréversible.</strong>
                    <br /><br />
                    Les factures, relevés bancaires et bulletins de salaire resteront 
                    disponibles dans leurs modules respectifs.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 hover:bg-red-700"
                    onClick={async () => {
                      setResetting(true);
                      try {
                        const res = await axios.post(
                          `${API}/api/comptabilite/reset-history`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success('Historique réinitialisé', {
                          description: res.data.message
                        });
                        fetchSettings();
                      } catch (error) {
                        toast.error('Erreur', {
                          description: error.response?.data?.detail || 'Impossible de réinitialiser'
                        });
                      }
                      setResetting(false);
                    }}
                  >
                    Confirmer la réinitialisation
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Save button at bottom */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={() => navigate('/comptabilite')}>
          Annuler
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder les paramètres
        </Button>
      </div>
    </div>
  );
}
